import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { X, Search, ChevronLeft } from "lucide-react";
import { fetchRecruiterSearch, fetchRecruiterSkills } from "../../../redux/actions/halloffame";
import { useDebounce2 } from "../../../hooks/useDebounce";
import { SET_SELECTED_FILTERS } from "../../../redux/actions/types";
import { Mixpanel } from "../../../config/Mixpanel";
import Constants from "../../../common/constants";

// const dummyInstitutions = ["IIT Madras", "IIT Bombay", "IIT Kanpur", "IIT Delhi", "IIT Roorkee", "IIT Kharagpur", "IIT Madras", "IIT Bombay", "IIT Kanpur"];

const Institution = ({ isOpen, onClose, onApplyFilters }) => {
    const dispatch = useDispatch();
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const selectedFilters = useSelector((store) => store?.hallOfFame?.selectedFilters);

    const [visibleInstitutions, setVisibleInstitutions] = useState([]);
    const [totalInstitutions, setTotalInstitutions] = useState([]);
    const [selectedInstitutions, setSelectedInstitutions] = useState(selectedFilters?.institutions || []);

    useEffect(() => {
        setSelectedInstitutions(selectedFilters?.institutions || []);
    }, [selectedFilters]);

    useEffect(() => {
        dispatch(fetchRecruiterSkills("institutions", (response) => {
            const newInstitutions = response?.values || [];
            setTotalInstitutions(prev => [...prev, ...newInstitutions]);
            setVisibleInstitutions(prev => [...prev, ...newInstitutions.slice(0, 20)]);
        }, (error) => {
            console.log(error);
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 50) {
            if (visibleInstitutions.length < totalInstitutions.length) {
                const nextBatch = totalInstitutions.slice(visibleInstitutions.length, visibleInstitutions.length + 20);
                setVisibleInstitutions(prev => [...prev, ...nextBatch]);
            }
        }
    };

    const { debouncedValue: debouncedSearchTerm } = useDebounce2(searchTerm, 500);

    const handleSearch = (e) => {
        Mixpanel.track(Constants.Mixpanel.RECRUITER_FILTER_DRAWER_SEARCH_BAR_CLICKED);
        setSearchTerm(e.target.value);
    };

    useEffect(() => {
        if (debouncedSearchTerm) {
            Mixpanel.track(Constants.Mixpanel.RECRUITER_FILTER_DRAWER_SEARCH_QUERY_SUBMITTED);
            dispatch(fetchRecruiterSearch("institutions", debouncedSearchTerm, 20, (response) => {
                setVisibleInstitutions(response?.results || []);
            }, (error) => {
                console.log(error);
            }));
        } else {
            if (totalInstitutions.length > 0) {
                setVisibleInstitutions(totalInstitutions.slice(0, 20));
            }
        }
    }, [debouncedSearchTerm, dispatch, totalInstitutions]);

    useEffect(() => {
        if (isOpen) {
            setShowModal(true);
            document.body.style.overflow = "hidden";
        } else {
            setTimeout(() => setShowModal(false), 300);
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    const toggleInstitution = (institution) => {
        setSelectedInstitutions(prev =>
            prev.includes(institution)
                ? prev.filter(item => item !== institution)
                : [...prev, institution]
        );
    };

    const handleApply = () => {
        Mixpanel.track(Constants.Mixpanel.RECRUITER_FILTER_DRAWER_APPLY_FILTERS_SUBMITTED);
        onClose();
        const newFilters = { institutions: [...(selectedFilters?.institutions || []), ...selectedInstitutions] };
        dispatch({ type: SET_SELECTED_FILTERS, payload: newFilters });
        onApplyFilters({ ...selectedFilters, ...newFilters });
    }

    const handleClear = () => {
        setSearchTerm("");
        Mixpanel.track(Constants.Mixpanel.RECRUITER_FILTER_DRAWER_CLEAR_FILTERS_CLICKED);
        setSelectedInstitutions([]);
        setVisibleInstitutions(totalInstitutions.slice(0, 20));
    };

    if (!showModal && !isOpen) return null;

    return ReactDOM.createPortal(
        <div
            className={`fixed inset-0 z-[100] flex items-end justify-center transition-all duration-300 md:items-center ${isOpen ? "visible" : "invisible"
                }`}
        >
            {/* Backdrop */}
            <div
                className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"
                    }`}
                onClick={onClose}
            />

            {/* Modal Container */}
            <div
                style={{ backgroundImage: "linear-gradient(180deg, #0B2435 0%, #0E3653 100%)" }}
                className={`relative z-10 w-full max-h-[90vh] md:max-h-[85vh] overflow-hidden flex flex-col md:w-[450px] text-white rounded-t-3xl md:rounded-2xl p-6 shadow-xl transition-all duration-300 transform ${isOpen ? "translate-y-0 opacity-100 scale-100" : "translate-y-full md:translate-y-10 opacity-0 md:scale-95"
                    }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <button
                        onClick={onClose}
                        className="flex items-center text-gray-300 hover:text-white transition-colors"
                    >
                        <ChevronLeft size={20} className="mr-1" />
                        <h2 className="text-2xl font-bold text-white">Institutions</h2>
                    </button>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-full hover:bg-white/10 transition-colors"
                    >
                        <X size={24} className="text-gray-400 hover:text-white" />
                    </button>
                </div>

                {/* Search Bar */}
                <div className="relative mb-4">
                    <input
                        type="text"
                        placeholder="Search By College"
                        value={searchTerm}
                        onChange={handleSearch}
                        className="w-full bg-transparent border border-[#4B617A] rounded-xl py-3 px-4 pr-10 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    />
                    <Search
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                        size={20}
                    />
                </div>

                {/* Navigation & Sort */}
                {/* <div className="flex items-center justify-between mb-6 border-b border-gray-700/50 pb-2">
                    <button
                        onClick={onBack}
                        className="flex items-center text-gray-300 hover:text-white transition-colors"
                    >
                        <ChevronLeft size={20} className="mr-1" />
                        <span>Pick A Institution</span>
                    </button>
                    <button className="text-[#00C1FF] font-medium hover:text-[#33cdff] transition-colors">
                        AZ
                    </button>
                </div> */}

                {/* Institutions Grid - Scrollable Area */}
                <div
                    className="flex-1 overflow-y-auto min-h-0 mb-6 scrollbar-hide"
                    onScroll={handleScroll}
                >
                    <div className="flex flex-wrap gap-3">
                        {visibleInstitutions?.length > 0 ? visibleInstitutions?.map((institution, index) => (
                            <button
                                key={index}
                                onClick={() => toggleInstitution(institution)}
                                className={`px-4 py-2 rounded-full border border-[#00C1FF] text-white text-sm transition-colors whitespace-nowrap ${selectedInstitutions.includes(institution) ? "bg-[#00C1FF] hover:bg-[#00C1FF]" : "bg-[#005f7e]/30 hover:bg-[#00C1FF]/20"
                                    }`}
                            >
                                {institution}
                            </button>
                        )) : (
                            <div className="flex items-center justify-center h-full w-full">
                                <p className="text-base font-medium text-center mt-2 text-gray-400 ">No Institutions found</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex gap-4 mt-auto pt-4">
                    <button
                        className="flex-1 bg-[#448B47] hover:bg-[#38753b] text-white font-semibold py-3 rounded-xl transition-colors"
                        onClick={() => handleApply()}
                    >
                        Apply
                    </button>
                    <button
                        onClick={handleClear}
                        className="flex-1 bg-[#1A3855] hover:bg-[#234668] text-white font-semibold py-3 rounded-xl transition-colors"
                    >
                        Clear
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default Institution;
